import React, { useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { usePageTransition, TextReveal } from '../PageTransition';
import { lunchMenu } from '../../data/menuData';
import { ChevronLeft, ChevronRight, Clock, Flame, Utensils, Gift } from 'lucide-react';
import ChefPickBadge from '../ChefPickBadge';
import { getLunchWeek } from '../../utils/lunchWeek';
import { useDailyMenus } from '../../hooks/useDailyMenus';
import { useLanguage } from '../../contexts/LanguageContext';
import { locText, origName, lunchUi, locBase } from '../../utils/lunchLocale';

const DAY_KEYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'weekend'];
const DAY_SHORT_FI = { monday: 'MA', tuesday: 'TI', wednesday: 'KE', thursday: 'TO', friday: 'PE', weekend: 'LA' };
const DAY_INDEX = { monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, weekend: 6 };

const DayCard = ({ dayKey, isToday, menu, lang }) => {
  const ui = lunchUi(lang);
  if (!menu) return null;
  const [titleMain, titleSub] = (locText(menu, 'day', lang)).split('·').map(s => s.trim());

  return (
    <div
      className={`snap-center shrink-0 w-[88vw] sm:w-[380px] md:w-[420px] bg-white rounded-2xl shadow-lg overflow-hidden flex flex-col border-2 ${
        isToday ? 'border-[#C97A2B]' : 'border-[#D9D9D6]'
      }`}
      data-testid={`home-day-card-${dayKey}`}
    >
      {/* Header */}
      <div className={`px-5 py-4 ${isToday ? 'bg-gradient-to-br from-[#C97A2B] to-[#B65A3A] text-white' : 'bg-[#1A1A1A] text-white'} relative overflow-hidden`}>
        <div className="absolute -top-6 -right-6 w-24 h-24 bg-white/10 rounded-full blur-2xl pointer-events-none" />
        <div className="relative flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <span className="text-[10px] tracking-[0.3em] uppercase font-bold text-white/80">{DAY_SHORT_FI[dayKey]}</span>
              {isToday && (
                <span className="flex items-center gap-1 text-[9px] uppercase font-bold tracking-wider bg-white/20 px-2 py-0.5 rounded-full">
                  <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> {ui.today}
                </span>
              )}
            </div>
            <h3 className="text-lg md:text-xl mt-1 leading-tight" style={{ fontFamily: 'DM Serif Display, serif' }}>{titleMain || menu.day}</h3>
            {titleSub && <p className="text-[11px] text-white/70 italic mt-0.5 leading-tight">{titleSub}</p>}
          </div>
          <div className="text-right text-white/80 text-[10px] shrink-0 flex items-center gap-1">
            <Clock className="w-3 h-3" /> {menu.hours.replace('-', '–')}
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="p-5 flex-1 space-y-4 max-h-[520px] overflow-y-auto">
        {/* Mains */}
        <div>
          <div className="flex items-center gap-1.5 mb-2">
            <Flame className="w-3.5 h-3.5 text-[#C97A2B]" />
            <p className="text-[10px] uppercase tracking-widest text-[#6B705C] font-bold">{ui.mains}</p>
          </div>
          <ul className="space-y-1.5">
            {(menu.mains || []).map((item, i) => (
              <li key={i} className="flex items-baseline gap-2">
                <span className="text-[#C97A2B] text-xs leading-none">•</span>
                <span className="flex-1 text-[13px] text-[#2F2F2F] leading-snug">
                  <span className="font-medium">{locText(item, 'name', lang)}</span>
                  {item.spicy && <span className="ml-1 text-xs">🌶</span>}
                  {item.dietary && item.dietary.length > 0 && (
                    <span className="ml-1 text-[10px] text-[#6B705C]">({item.dietary.join(', ')})</span>
                  )}
                  {item.chefPick && <span className="ml-1.5 align-middle"><ChefPickBadge testId={`home-chef-pick-${dayKey}`} label={ui.chefPick} /></span>}
                  {origName(item, lang) && <span className="block text-[10px] text-[#9A9A94] italic">{origName(item, lang)}</span>}
                </span>
              </li>
            ))}
          </ul>
        </div>

        {/* Sides */}
        {menu.sides && menu.sides.length > 0 && (
          <div>
            <div className="flex items-center gap-1.5 mb-2">
              <Utensils className="w-3.5 h-3.5 text-[#C97A2B]" />
              <p className="text-[10px] uppercase tracking-widest text-[#6B705C] font-bold">{ui.sides}</p>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {menu.sides.map((s, i) => (
                <span key={i} className="text-[11px] text-[#2F2F2F] bg-[#F5F3EF] border border-[#D9D9D6] px-2 py-0.5 rounded-full">
                  {locText(s, 'name', lang)}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Desserts */}
        {menu.desserts && menu.desserts.length > 0 && (
          <div>
            <div className="flex items-center gap-1.5 mb-2">
              <Gift className="w-3.5 h-3.5 text-[#C97A2B]" />
              <p className="text-[10px] uppercase tracking-widest text-[#6B705C] font-bold">{ui.dessert}</p>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {menu.desserts.map((d, i) => (
                <span key={i} className="text-[11px] text-[#C97A2B] bg-[#C97A2B]/10 border border-[#C97A2B]/25 px-2 py-0.5 rounded-full">
                  {locText(d, 'name', lang)}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const TodaysLunchSection = () => {
  const { t } = useTranslation();
  const { navigateWithTransition } = usePageTransition();
  const scrollerRef = useRef(null);
  const dailyMenus = useDailyMenus();
  const { currentLanguage: lang } = useLanguage();

  // Helsinki time
  const helsinkiDow = (() => {
    try {
      const w = new Intl.DateTimeFormat('en-US', { timeZone: 'Europe/Helsinki', weekday: 'short' }).format(new Date());
      return { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 }[w] ?? new Date().getDay();
    } catch { return new Date().getDay(); }
  })();
  const todayKey = { 0: 'weekend', 1: 'monday', 2: 'tuesday', 3: 'wednesday', 4: 'thursday', 5: 'friday', 6: 'weekend' }[helsinkiDow];

  // Scroll to today on mount
  useEffect(() => {
    const t = setTimeout(() => {
      const el = scrollerRef.current?.querySelector(`[data-testid="home-day-card-${todayKey}"]`);
      if (el && scrollerRef.current) {
        scrollerRef.current.scrollTo({ left: el.offsetLeft - 16, behavior: 'smooth' });
      }
    }, 300);
    return () => clearTimeout(t);
  }, [todayKey]);

  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const amount = el.clientWidth * 0.85 * dir;
    el.scrollBy({ left: amount, behavior: 'smooth' });
  };

  const regular = lunchMenu.priceInfo.regular;
  const weekend = lunchMenu.priceInfo.weekend.regular;

  return (
    <section className="relative py-12 md:py-16 bg-[#F5F3EF]" data-testid="todays-lunch-section">
      <div className="max-w-[1400px] mx-auto">
        <div className="text-center mb-6 md:mb-8 px-4 md:px-12">
          <TextReveal><p className="text-[#C97A2B] text-xs tracking-[0.3em] uppercase mb-2" data-testid="home-week-label">{lunchUi(lang).buffet} · {getLunchWeek(new Date(), lang).label}</p></TextReveal>
          <TextReveal delay={0.1}>
            <h2 className="text-3xl md:text-4xl lg:text-5xl text-[#2F2F2F]" style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}>
              Tutustu <span className="italic text-[#C97A2B]">koko viikon</span> lounaslistaan
            </h2>
          </TextReveal>
          <p className="text-[#6B705C] text-sm mt-2">
            Ma–Pe {regular} · La {weekend} · Lapset alle 6 v. ilmaiseksi
          </p>
        </div>

        {/* 16+ Salads Highlight */}
        {lunchMenu.saladHighlight && (
          <div className="mx-4 md:mx-12 mb-6 bg-gradient-to-r from-[#F7E9A0]/40 via-white to-[#C97A2B]/5 border-2 border-[#C97A2B]/25 rounded-2xl p-4 md:p-5 flex items-center gap-4 relative overflow-hidden" data-testid="home-salad-highlight">
            <div className="absolute -top-6 -right-6 w-32 h-32 bg-green-500/10 rounded-full blur-2xl pointer-events-none" />
            <div className="hidden sm:flex w-12 h-12 rounded-full bg-[#C97A2B]/15 border-2 border-[#C97A2B]/40 items-center justify-center flex-shrink-0 text-xl">🥗</div>
            <div className="relative flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2 mb-0.5">
                <span className="text-green-700 text-[10px] tracking-[0.25em] uppercase font-bold">{locBase(lunchMenu.saladHighlight, 'title', lang)}</span>
                <span className="text-[9px] bg-green-600 text-white px-2 py-0.5 rounded-full font-bold uppercase tracking-wider">{lunchUi(lang).fresh}</span>
              </div>
              <p className="text-[#2F2F2F] text-[13px] md:text-sm leading-snug">
                {locBase(lunchMenu.saladHighlight, 'subtitle', lang)} — {locBase(lunchMenu.saladHighlight, 'description', lang)}
              </p>
            </div>
          </div>
        )}

        {/* Scroller nav arrows (desktop) */}
        <div className="hidden md:flex justify-end gap-2 px-12 mb-3">
          <button onClick={() => scrollBy(-1)} aria-label="Edellinen" data-testid="scroll-prev"
            className="w-10 h-10 rounded-full bg-white border border-[#D9D9D6] text-[#2F2F2F] hover:bg-[#C97A2B] hover:text-white hover:border-[#C97A2B] transition-colors flex items-center justify-center shadow-sm">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button onClick={() => scrollBy(1)} aria-label="Seuraava" data-testid="scroll-next"
            className="w-10 h-10 rounded-full bg-white border border-[#D9D9D6] text-[#2F2F2F] hover:bg-[#C97A2B] hover:text-white hover:border-[#C97A2B] transition-colors flex items-center justify-center shadow-sm">
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        {/* Horizontal snap scroller */}
        <div
          ref={scrollerRef}
          className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-4 px-4 md:px-12 scroll-smooth"
          style={{ scrollbarWidth: 'thin' }}
          data-testid="lunch-day-scroller"
        >
          {DAY_KEYS.map((dayKey) => (
            dailyMenus[dayKey] && <DayCard key={dayKey} dayKey={dayKey} menu={dailyMenus[dayKey]} lang={lang} isToday={DAY_INDEX[dayKey] === helsinkiDow} />
          ))}
        </div>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-6 px-4">
          <motion.button
            onClick={() => navigateWithTransition('/menu')}
            whileHover={{ scale: 1.02 }}
            className="w-full sm:w-auto px-7 py-3.5 bg-[#C97A2B] text-white rounded-full text-xs font-bold tracking-[0.15em] uppercase hover:bg-[#B65A3A] transition-colors shadow-md"
            data-testid="view-full-lunch-btn"
          >
            Katso koko viikon lounaslista →
          </motion.button>
          <motion.button
            onClick={() => navigateWithTransition('/ehdota')}
            whileHover={{ scale: 1.02 }}
            className="w-full sm:w-auto px-7 py-3.5 bg-white border border-[#D9D9D6] text-[#2F2F2F] rounded-full text-xs font-semibold tracking-[0.1em] uppercase hover:border-[#C97A2B] hover:text-[#C97A2B] transition-colors"
          >
            🍲 Ehdota ruokaa
          </motion.button>
        </div>
      </div>
    </section>
  );
};

export default TodaysLunchSection;
